import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'motion/react';
import {
  CheckCircle, Edit3, Sparkles, Award, TrendingUp, Users, Shield,
  Globe, ArrowRight, Star, AlertCircle,
} from 'lucide-react';
import { TURNOVER_RANGES } from './CrossbaseZambiaData';

const ZM_GREEN = '#0d7a3e';
const ZM_ORANGE = '#f5821f';
const ZM_BLACK = '#1a1a2e';
const ZM_BLUE = '#1565c0';
const ZM_LIGHT = '#f0f4f0';
const ZM_GRAY = '#64748b';

const PROFILE_STORAGE_KEY = 'crossbase_sme_profile_v2';

export const DEFAULT_PROFILE = {
  // Identity
  name: '', tradingName: '', regNumber: '', tpin: '', yearEstablished: '', legalForm: '', logoData: '',
  // PACRA registration (Step 0)
  registeredPACRA: '', pacraCertData: '', pacraCertName: '',
  // Owner & demographics
  ownerName: '', ownerGender: '', ownerAge: '',
  // Classification (ISIC Rev 4)
  sector: '', sectorCode: '', subSector: '', womenLed: '', youthLed: '', yearsInOperation: '',
  // Location
  province: '', district: '', address: '', urbanRural: '', gpsLat: '', gpsLng: '',
  // Financials
  turnoverRange: '', category: '', // category auto-derived from turnover range
  turnover: '', monthlyRevenue: '', costRent: '', costSalaries: '', costInventory: '', profitMargin: '', growthRate: '',
  employees: '', employeesFullTime: '', employeesPartTime: '', fixedInvestments: '',
  // Digital presence
  email: '', phone: '', website: '', facebook: '', whatsapp: '',
  digitalMaturity: '',
  // Operations (lean)
  acceptsMobileMoney: '', mobileMoneyMTN: false, mobileMoneyAirtel: false, mobileMoneyZamtel: false,
  usesAccountingTools: '', hasInternetAccess: '',
  accessToFinance: '',
  // Goals / intent
  wantsFunding: '', wantsCustomers: '', wantsDigitize: '', wantsHireEmployees: '', wantsExport: '',
  // Profile description
  products: '', mainMarket: '', challenges: '',
  // Meta
  registered: false,
  registeredAt: '',
};

export const PROFILE_STEPS = [
  'Identity',
  'Classification',
  'Location',
  'Financials',
  'Digital & Operations',
  'Business Goals',
  'Business Profile',
];

export const useSMEProfile = () => {
  const [profile, setProfile] = useState(() => {
    try {
      const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
      return raw ? { ...DEFAULT_PROFILE, ...JSON.parse(raw) } : DEFAULT_PROFILE;
    } catch {
      return DEFAULT_PROFILE;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile)); } catch {}
  }, [profile]);
  const update = (k, v) => setProfile(p => ({ ...p, [k]: v }));
  const updateMany = (patch) => setProfile(p => ({ ...p, ...patch }));
  const reset = () => setProfile(DEFAULT_PROFILE);
  return { profile, setProfile, update, updateMany, reset };
};

/* ═══════════════════════════════════════════
   PROFILE COMPLETENESS
   ═══════════════════════════════════════════ */

const REQUIRED_FIELDS = [
  'name', 'registeredPACRA', 'sector', 'turnoverRange', 'category', 'womenLed', 'province', 'district', 'address',
  'employees', 'phone',
  'yearsInOperation', 'monthlyRevenue', 'profitMargin',
  'acceptsMobileMoney', 'usesAccountingTools', 'hasInternetAccess', 'accessToFinance',
  'wantsFunding', 'wantsCustomers', 'wantsDigitize', 'wantsHireEmployees', 'wantsExport',
  'products', 'mainMarket',
];

export const profileCompleteness = (profile) => {
  const filled = REQUIRED_FIELDS.filter(f => {
    const v = profile[f];
    return v !== '' && v !== null && v !== undefined;
  }).length;
  return Math.round((filled / REQUIRED_FIELDS.length) * 100);
};

/* ═══════════════════════════════════════════
   FUNDING MATCH ENGINE
   Pure rule-based — no API. Demo only.
   ═══════════════════════════════════════════ */

const PROGRAMS = [
  {
    id: 'ceec',
    name: 'CEEC Empowerment Fund',
    authority: 'Citizens Economic Empowerment Commission',
    description: 'Loans and grants for SMEs, with priority for women, youth and rural enterprises. Up to ZMW 500,000.',
    color: ZM_GREEN,
    icon: Award,
    tags: ['Loan', 'Grant', 'Open'],
    actionLabel: 'Apply via CEEC',
    rules: (p) => {
      let score = 30;
      if (p.registeredPACRA === 'Yes') score += 15;
      if (p.womenLed === 'Yes') score += 20;
      if (p.youthLed === 'Yes') score += 20;
      if (p.urbanRural === 'Rural') score += 10;
      if (['Micro', 'Small'].includes(p.category)) score += 15;
      if (p.wantsFunding === 'Yes') score += 10;
      if (Number(p.yearsInOperation) >= 1) score += 10;
      return Math.min(100, score);
    },
    eligibility: ['Registered Zambian SME', 'TPIN required', '≥ 1 year in operation', 'Priority: women, youth, rural'],
  },
  {
    id: 'cdf',
    name: 'Constituency Development Fund (CDF)',
    authority: 'Local Council / Constituency Office',
    description: 'Local funding via councils for community-based SMEs and projects.',
    color: ZM_BLUE,
    icon: TrendingUp,
    tags: ['Grant', 'Local'],
    actionLabel: 'Contact Local Council',
    rules: (p) => {
      let score = 25;
      if (['Micro', 'Small'].includes(p.category)) score += 25;
      if (p.urbanRural === 'Rural') score += 20;
      if (p.registeredPACRA === 'Yes') score += 10;
      if (p.wantsFunding === 'Yes') score += 10;
      if (p.district) score += 10;
      return Math.min(100, score);
    },
    eligibility: ['Local council registration', 'Community-based SME', 'Project plan'],
  },
  {
    id: 'youth',
    name: 'Youth Enterprise Fund',
    authority: 'Ministry of SMEs',
    description: 'Special funding and training for youth-led startups (founder ≤ 35 years).',
    color: ZM_ORANGE,
    icon: Sparkles,
    tags: ['Grant', 'Training'],
    actionLabel: 'Register Interest',
    rules: (p) => {
      let score = 10;
      if (p.youthLed === 'Yes') score += 50;
      if (Number(p.ownerAge) > 0 && Number(p.ownerAge) <= 35) score += 25;
      if (p.wantsFunding === 'Yes') score += 10;
      if (p.category === 'Micro') score += 10;
      return Math.min(100, p.youthLed === 'No' ? 0 : score);
    },
    eligibility: ['Youth-led enterprise (founder ≤ 35)', 'Registered with PACRA', 'Business plan'],
  },
  {
    id: 'women',
    name: 'Women in Business Program',
    authority: 'Ministry of SMEs',
    description: 'Mentorship, financing and networking for women-led enterprises.',
    color: '#be185d',
    icon: Users,
    tags: ['Mentorship', 'Funding'],
    actionLabel: 'Apply',
    rules: (p) => {
      let score = 10;
      if (p.womenLed === 'Yes') score += 50;
      if (p.ownerGender === 'Female') score += 20;
      if (p.wantsFunding === 'Yes') score += 10;
      if (['Micro', 'Small'].includes(p.category)) score += 15;
      return Math.min(100, p.womenLed === 'No' ? 0 : score);
    },
    eligibility: ['Women-led SME (≥ 50% women ownership)', 'Registered enterprise', 'Active business operations'],
  },
  {
    id: 'export',
    name: 'Export Readiness Training',
    authority: 'Ministry of SMEs + ZDA',
    description: 'Training on standards, packaging and trade regulations to prepare for international markets.',
    color: '#7c3aed',
    icon: Globe,
    tags: ['Training', 'Next intake: June 2026'],
    actionLabel: 'Reserve a seat',
    rules: (p) => {
      let score = 10;
      if (p.registeredPACRA === 'Yes') score += 10;
      if (Number(p.yearsInOperation) >= 2) score += 15;
      if (['Small', 'Medium'].includes(p.category)) score += 15;
      if (p.wantsExport === 'Yes') score += 50;
      if (p.mainMarket && p.mainMarket.toLowerCase().includes('export')) score += 10;
      return Math.min(100, score);
    },
    eligibility: ['SME planning to export', '≥ 2 years operation', 'Quality control in place'],
  },
  {
    id: 'tax',
    name: 'SME Tax Awareness Workshop',
    authority: 'Ministry of SMEs + ZRA',
    description: 'Free workshops on TPIN, Smart Invoice and tax compliance for SMEs.',
    color: '#0891b2',
    icon: CheckCircle,
    tags: ['Free Workshop', 'Ongoing'],
    actionLabel: 'Find next workshop',
    rules: (p) => {
      let score = 40;
      if (!p.tpin) score += 20;
      if (p.registeredPACRA !== 'Yes') score += 20;
      if (p.usesAccountingTools === 'No') score += 15;
      return Math.min(100, score);
    },
    eligibility: ['Any registered or unregistered SME', 'Open to all sectors'],
  },
  {
    id: 'zda',
    name: 'ZDA Investment Certificate',
    authority: 'Zambia Development Agency',
    description: 'Tax incentives, investment protection and faster approvals (major incentives ≥ USD 500,000).',
    color: '#1e40af',
    icon: Shield,
    tags: ['Incentives', 'Investment'],
    actionLabel: 'Start application',
    rules: (p) => {
      let score = 20;
      if (Number(p.turnover) >= 1_000_000) score += 30;
      if (p.category === 'Medium') score += 20;
      if (p.wantsExport === 'Yes') score += 15;
      if (p.wantsFunding === 'Yes') score += 10;
      if (p.registeredPACRA === 'Yes') score += 15;
      return Math.min(100, score);
    },
    eligibility: ['Formal company', 'Sector-aligned investment', 'Business plan & financial proof'],
  },
  {
    id: 'dbz',
    name: 'Development Bank of Zambia (DBZ)',
    authority: 'Development Bank of Zambia',
    description: 'Long-term SME and project financing for capital-intensive ventures.',
    color: '#15803d',
    icon: TrendingUp,
    tags: ['Long-term Loan'],
    actionLabel: 'Speak to DBZ',
    rules: (p) => {
      let score = 15;
      if (p.wantsFunding === 'Yes') score += 25;
      if (p.category === 'Medium') score += 20;
      if (Number(p.yearsInOperation) >= 3) score += 20;
      if (p.registeredPACRA === 'Yes') score += 15;
      if (p.accessToFinance === 'No') score += 10;
      return Math.min(100, score);
    },
    eligibility: ['Formal SME', '≥ 3 years operation', 'Audited financials'],
  },
  {
    id: 'digitize',
    name: 'SME Digital Transformation Program',
    authority: 'Ministry of SMEs',
    description: 'Subsidised software, digital tools, and training to digitize operations and accept digital payments.',
    color: '#0d9488',
    icon: Sparkles,
    tags: ['Training', 'Subsidy'],
    actionLabel: 'Join the program',
    rules: (p) => {
      let score = 20;
      if (p.wantsDigitize === 'Yes') score += 35;
      if (p.digitalMaturity === 'none') score += 25;
      if (p.digitalMaturity === 'basic') score += 15;
      if (p.acceptsMobileMoney === 'No') score += 10;
      if (p.usesAccountingTools === 'No') score += 10;
      return Math.min(100, score);
    },
    eligibility: ['SMEs with low digital maturity', 'Open to all sectors', 'Owner motivation required'],
  },
];

export const matchPrograms = (profile) => {
  return PROGRAMS
    .map(p => ({ ...p, score: p.rules(profile) }))
    .sort((a, b) => b.score - a.score);
};

export const ALL_PROGRAMS = PROGRAMS;

/* ═══════════════════════════════════════════
   PROFILE OVERVIEW (Dashboard)
   ═══════════════════════════════════════════ */

const ProfileSection = ({ title, items, onEdit, accent }) => (
  <div className="rounded-xl bg-white shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
      <div className="flex items-center gap-2">
        <div className="w-1 h-4 rounded-full" style={{ background: accent || ZM_GREEN }} />
        <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: ZM_BLACK }}>{title}</h4>
      </div>
      <button onClick={onEdit} data-testid={`profile-edit-${title.toLowerCase().replace(/\s/g, '-')}`} className="flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-lg transition-all hover:brightness-95" style={{ background: ZM_LIGHT, color: ZM_GREEN }}>
        <Edit3 size={10} /> Edit
      </button>
    </div>
    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
      {items.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <div className="text-[10px] font-medium uppercase tracking-wide" style={{ color: ZM_GRAY }}>{label}</div>
          <div className={`text-sm truncate ${value ? 'font-semibold' : 'italic'}`} style={{ color: value ? ZM_BLACK : ZM_GRAY }}>
            {value || 'Not provided'}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const yesNoLabel = (v) => v || 'Not declared';
const fmtMoney = (v) => v ? `ZMW ${Number(v).toLocaleString()}` : '';

export const RegisteredProfile = ({ profile, onEditStep, onOpenPrograms, onOpenFullEdit }) => {
  const completeness = profileCompleteness(profile);
  const matches = useMemo(() => matchPrograms(profile), [profile]);
  const top3 = matches.slice(0, 3);

  const sections = [
    {
      title: 'Identity', step: 0, accent: ZM_GREEN,
      items: [
        ['Enterprise Name', profile.name],
        ['Trading Name', profile.tradingName],
        ['Registered with PACRA', yesNoLabel(profile.registeredPACRA)],
        ['PACRA Certificate', profile.pacraCertName || (profile.pacraCertData ? 'Attached' : '')],
        ['Registration No.', profile.regNumber],
        ['TPIN', profile.tpin],
        ['Year Established', profile.yearEstablished],
        ['Legal Form', profile.legalForm],
        ['Owner', profile.ownerName],
        ['Owner Gender / Age', [profile.ownerGender, profile.ownerAge].filter(Boolean).join(' · ')],
      ],
    },
    {
      title: 'Classification', step: 1, accent: ZM_BLUE,
      items: [
        ['Sector', profile.sector ? `${profile.sectorCode || ''} ${profile.sector}`.trim() : ''],
        ['Sub-Sector', profile.subSector],
        ['Years in Operation', profile.yearsInOperation],
        ['Women-Led', yesNoLabel(profile.womenLed)],
        ['Youth-Led', yesNoLabel(profile.youthLed)],
      ],
    },
    {
      title: 'Location', step: 2, accent: ZM_ORANGE,
      items: [
        ['Province', profile.province], ['District', profile.district],
        ['Physical Address', profile.address], ['Urban / Rural', profile.urbanRural],
        ['GPS', [profile.gpsLat, profile.gpsLng].filter(Boolean).join(', ')],
      ],
    },
    {
      title: 'Financials', step: 3, accent: '#7c3aed',
      items: [
        ['Annual Turnover Range', profile.turnoverRange ? (TURNOVER_RANGES.find(r => r.value === profile.turnoverRange)?.label || profile.turnoverRange) : ''],
        ['SME Category', profile.category],
        ['Monthly Revenue', fmtMoney(profile.monthlyRevenue)],
        ['Profit Margin', profile.profitMargin ? `${profile.profitMargin}%` : ''],
        ['Growth Rate', profile.growthRate ? `${profile.growthRate}%` : ''],
        ['Cost — Rent', fmtMoney(profile.costRent)],
        ['Cost — Salaries', fmtMoney(profile.costSalaries)],
        ['Cost — Inventory', fmtMoney(profile.costInventory)],
        ['Total Fixed Investments', fmtMoney(profile.fixedInvestments)],
        ['Employees', profile.employees],
        ['Full-Time / Part-Time', [profile.employeesFullTime, profile.employeesPartTime].filter(Boolean).join(' / ')],
      ],
    },
    {
      title: 'Digital & Operations', step: 4, accent: '#0891b2',
      items: [
        ['Email', profile.email], ['Phone', profile.phone],
        ['WhatsApp', profile.whatsapp], ['Website', profile.website],
        ['Facebook', profile.facebook],
        ['Digital Maturity', profile.digitalMaturity],
        ['Internet Access', yesNoLabel(profile.hasInternetAccess)],
        ['Accepts Mobile Money', yesNoLabel(profile.acceptsMobileMoney)],
        ['Mobile Money Providers', [
          profile.mobileMoneyMTN && 'MTN',
          profile.mobileMoneyAirtel && 'Airtel',
          profile.mobileMoneyZamtel && 'Zamtel',
        ].filter(Boolean).join(', ')],
        ['Uses Accounting Tools', yesNoLabel(profile.usesAccountingTools)],
        ['Access to Finance', yesNoLabel(profile.accessToFinance)],
      ],
    },
    {
      title: 'Business Goals', step: 5, accent: '#dc2626',
      items: [
        ['Wants Funding', yesNoLabel(profile.wantsFunding)],
        ['Wants More Customers', yesNoLabel(profile.wantsCustomers)],
        ['Wants to Digitize', yesNoLabel(profile.wantsDigitize)],
        ['Wants to Hire Employees', yesNoLabel(profile.wantsHireEmployees)],
        ['Wants to Export', yesNoLabel(profile.wantsExport)],
      ],
    },
    {
      title: 'Business Profile', step: 6, accent: ZM_GREEN,
      items: [
        ['Products / Services', profile.products],
        ['Main Market', profile.mainMarket],
        ['Key Challenges', profile.challenges],
      ],
    },
  ];

  return (
    <div data-testid="registered-profile">
      {/* Header / completeness */}
      <div className="rounded-2xl p-5 mb-5 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${ZM_GREEN} 0%, #0a5c2e 100%)` }}>
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-15" style={{ background: ZM_ORANGE }} />
        <div className="relative z-10 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold mb-2" style={{ background: 'rgba(255,255,255,0.2)' }}>
              <CheckCircle size={11} /> Registered Enterprise
            </div>
            <h2 className="text-xl sm:text-2xl font-bold mb-1 truncate">{profile.name || 'Your Enterprise'}</h2>
            <p className="text-xs opacity-80 mb-3">{[profile.sector, profile.category, profile.province].filter(Boolean).join(' · ') || 'SME registered on Crossbase'}</p>
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium opacity-80">Profile completeness</span>
              <span className="text-2xl font-bold tabular-nums">{completeness}%</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.25)' }}>
              <motion.div className="h-full rounded-full" style={{ background: ZM_ORANGE }} initial={{ width: 0 }} animate={{ width: `${completeness}%` }} transition={{ duration: 0.6 }} />
            </div>
            <p className="text-[10px] mt-1.5 opacity-80">{completeness === 100 ? 'Complete profile — every program is unlocked.' : 'Add missing data to unlock more funding programs.'}</p>
          </div>
          <button onClick={onOpenFullEdit} data-testid="open-full-edit-btn" className="flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-lg flex-shrink-0" style={{ background: 'rgba(255,255,255,0.2)' }}>
            <Edit3 size={12} /> Edit Profile
          </button>
        </div>
      </div>

      {/* Funding eligibility – top 3 */}
      <div className="rounded-2xl bg-white shadow-sm p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: `${ZM_ORANGE}15` }}><Star size={14} style={{ color: ZM_ORANGE }} /></div>
              <h3 className="text-sm font-bold" style={{ color: ZM_BLACK }}>Your Funding Match</h3>
            </div>
            <p className="text-[11px]" style={{ color: ZM_GRAY }}>Based on your profile data, you may qualify for the following:</p>
          </div>
          <button onClick={onOpenPrograms} data-testid="see-all-programs-btn" className="text-xs font-semibold flex items-center gap-1" style={{ color: ZM_GREEN }}>
            See all <ArrowRight size={11} />
          </button>
        </div>
        <div className="space-y-2">
          {top3.map(p => {
            const Icon = p.icon;
            const tier = p.score >= 70 ? 'Strong match' : p.score >= 40 ? 'Possible match' : 'Low match';
            const tierColor = p.score >= 70 ? ZM_GREEN : p.score >= 40 ? ZM_ORANGE : ZM_GRAY;
            return (
              <button key={p.id} onClick={onOpenPrograms} className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all hover:bg-gray-50" style={{ border: `1px solid ${p.color}20`, background: `${p.color}08` }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${p.color}15` }}>
                  <Icon size={16} style={{ color: p.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate" style={{ color: ZM_BLACK }}>{p.name}</div>
                  <div className="text-[10px] truncate" style={{ color: ZM_GRAY }}>{p.authority}</div>
                </div>
                <div className="flex flex-col items-end flex-shrink-0">
                  <div className="text-base font-bold tabular-nums" style={{ color: tierColor }}>{p.score}%</div>
                  <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: tierColor }}>{tier}</div>
                </div>
              </button>
            );
          })}
        </div>
        {completeness < 80 && (
          <div className="mt-3 flex items-start gap-2 p-2.5 rounded-lg" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
            <AlertCircle size={13} style={{ color: '#92400e' }} className="mt-0.5 flex-shrink-0" />
            <p className="text-[11px]" style={{ color: '#92400e' }}>Complete more of your profile to refine your funding matches and unlock additional programs.</p>
          </div>
        )}
      </div>

      {/* Profile sections */}
      <div className="space-y-3">
        {sections.map(sec => (
          <ProfileSection key={sec.title} title={sec.title} items={sec.items} accent={sec.accent} onEdit={() => onEditStep(sec.step)} />
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   PROGRAMS / FUNDING MATCH FULL VIEW
   ═══════════════════════════════════════════ */

export const FundingMatchView = ({ profile, onBack, onSelect }) => {
  const matches = useMemo(() => matchPrograms(profile), [profile]);
  const completeness = profileCompleteness(profile);

  return (
    <div data-testid="funding-match-view">
      <div className="rounded-2xl p-5 mb-5 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${ZM_GREEN} 0%, #0a5c2e 100%)` }}>
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-15" style={{ background: ZM_ORANGE }} />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold mb-2" style={{ background: 'rgba(255,255,255,0.2)' }}>
            <Star size={11} /> Smart Funding Match
          </div>
          <h2 className="text-xl font-bold mb-1">Programs matched to your profile</h2>
          <p className="text-xs opacity-90">Each program is scored 0–100 % based on your enterprise data, ownership and goals. Higher % = stronger fit.</p>
          <p className="text-[11px] opacity-80 mt-2">Profile completeness: <strong>{completeness}%</strong> — completing more fields refines your matches.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {matches.map(p => {
          const Icon = p.icon;
          const tier = p.score >= 70 ? 'Strong match' : p.score >= 40 ? 'Possible match' : 'Low match';
          const tierColor = p.score >= 70 ? ZM_GREEN : p.score >= 40 ? ZM_ORANGE : ZM_GRAY;
          return (
            <motion.button
              key={p.id}
              whileHover={{ y: -2 }}
              onClick={() => onSelect && onSelect(p)}
              className="text-left p-4 rounded-xl bg-white shadow-sm transition-all hover:shadow-md"
              style={{ border: `1px solid ${p.color}25` }}
              data-testid={`program-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${p.color}15` }}>
                  <Icon size={18} style={{ color: p.color }} />
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xl font-bold tabular-nums" style={{ color: tierColor }}>{p.score}%</div>
                  <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: tierColor }}>{tier}</div>
                </div>
              </div>
              <h3 className="text-sm font-bold mb-1" style={{ color: ZM_BLACK }}>{p.name}</h3>
              <p className="text-[10px] mb-2" style={{ color: ZM_GRAY }}>{p.authority}</p>
              <p className="text-xs leading-relaxed mb-3" style={{ color: ZM_GRAY }}>{p.description}</p>
              <div className="h-1.5 rounded-full overflow-hidden mb-3" style={{ background: '#f1f5f9' }}>
                <div className="h-full rounded-full" style={{ width: `${p.score}%`, background: tierColor }} />
              </div>
              <div className="flex flex-wrap gap-1">
                {p.tags.map((t, i) => <span key={i} className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ background: `${p.color}15`, color: p.color }}>{t}</span>)}
              </div>
            </motion.button>
          );
        })}
      </div>

      {onBack && (
        <button onClick={onBack} className="mt-5 text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ background: ZM_LIGHT, color: ZM_GRAY }}>
          ← Back
        </button>
      )}
    </div>
  );
};
